import debounce from "lodash.debounce";
import { useEffect, useRef } from "react";
import { UseFormWatch } from "react-hook-form";
import { TimelineVariant, deformatCurrency } from "@trussle/external-components";
import { ILoggedInStatus } from "../../LoggedInStatus";
import { getRepayments, useSaveHomeApiValues } from "./queriesAndMutations";
import { IRateWatchPanelFormValues } from ".";

const MILISECONDS_TO_DEBOUNCE = 1000;

export const useDebounceValues = (
  watch: UseFormWatch<IRateWatchPanelFormValues>,
  contactId: string,
  status: ILoggedInStatus,
  endDateValue: Date,
  isLoading: boolean,
) => {
  const repayment = watch("repayment");
  const balance = watch("balance");
  const propertyValue = watch("propertyValue");
  const currentTerm = watch("currentTerm");
  const initialLoading = useRef(true);

  const [saveHomeValues] = useSaveHomeApiValues();

  const debounced = useRef(debounce(async (contactId, status, balance, repayment, propertyValue, endDateValue, currentTerm) => {
    if (status === ILoggedInStatus.LOGGED_IN) {
      saveHomeValues({
        variables: {
          editHomeParams: {
            contactId,
            productEndDate: endDateValue,
            propertyValue: propertyValue ? Number(deformatCurrency(propertyValue)) : null,
            balance: balance ? Number(deformatCurrency(balance)) : null,
            currentMonthlyRepayment: repayment ? Number(deformatCurrency(repayment)) : null,
            currentTerm: currentTerm ? Number(currentTerm) : null,
          },
        },
      });
    }
  }, MILISECONDS_TO_DEBOUNCE));
  useEffect(() => {
    if (!isLoading) {
      if (!initialLoading.current) {
        debounced.current(contactId, status, balance, repayment, propertyValue, endDateValue, currentTerm);
      }
      initialLoading.current = false;
    }
  }, [contactId, status, balance, repayment, propertyValue, isLoading, endDateValue.toISOString(), currentTerm]);
};

export const useDebounceRepayment = (
  watch: UseFormWatch<IRateWatchPanelFormValues>,
  setCheapestRepayment: (params: { initialPeriodRate: number, monthlyRepayment: number }) => void,
  setCheapestRepaymentLoading: (loading: boolean) => void,
  timelineVariant: TimelineVariant,
) => {
  const balance = watch("balance");
  const propertyValue = watch("propertyValue");
  const currentTerm = watch("currentTerm");
  const debounced = useRef(debounce(async (balance, propertyValue, timelineVariant, currentTerm) => {
    if (balance && propertyValue && timelineVariant === TimelineVariant.Early) {
      const response = await getRepayments(Number(deformatCurrency(propertyValue)), Number(deformatCurrency(balance)), Number(currentTerm));
      const { data: { getBestMonthlyRepayments: { initialPeriodRate, monthlyRepayment } } } = response;
      setCheapestRepayment({ initialPeriodRate, monthlyRepayment });
      setCheapestRepaymentLoading(false);
    }
  }, MILISECONDS_TO_DEBOUNCE));
  useEffect(() => {
    setCheapestRepaymentLoading(true);
    debounced.current(balance, propertyValue, timelineVariant, currentTerm);
  }, [balance, propertyValue, timelineVariant, currentTerm]);
};
